// How far along one form step is.
//
// The values double as CSS class names. See the ng-class expressions in settings.html and
// progress_nav.html, which apply the progress value to the nav item for each step.
//
// This lives here rather than in FormConfigs on purpose. FormConfigs and Program import each other,
// so a runtime import of FormConfigs from this module would break that cycle depending on which
// module a consumer loads first. This module imports the FormConfig type only, which erases at
// compile time.
export enum FormProgress {
    complete = 'complete',
    incomplete = 'incomplete',
    // The step passes its validations, but the user has not visited it yet. Only steps with
    // `requiresVisit` can be in this state.
    none = 'none',
}

/**
 * One failed validation, addressed by the path the validation schema uses.
 *
 * The path is the key in `FormCompletionInput.formValues`, so it has no `careerProfile.` or
 * `programFamilyFormDatum.` prefix. A nested path uses Yup's dotted form, for example
 * `education_experiences[0].educational_organization`.
 */
export type FormValidationError = {
    path: string;
    // The name of the failing Yup test, when it has one. The error list reads this to group a
    // language failure apart from an incomplete field. Yup leaves it undefined for some built-in
    // checks, so treat an absent value as "no special handling".
    type?: string;
};

/**
 * Everything the completion functions need to judge a set of forms.
 *
 * This type exists so the completion math is a pure function of its arguments. The functions used to
 * read `ClientStorage` and reach into Iguana models directly, which made them impossible to test
 * without a browser and impossible to reuse from React. Build one of these with
 * `buildFormCompletionInput`, or construct it directly from react-hook-form values.
 */
export type FormCompletionInput = {
    // The combined values the validation schemas run against. The application forms write to two
    // models - CareerProfile for everything shared between program families, and
    // ProgramFamilyFormDatum for everything specific to one program family. A form can write to
    // either, so the caller merges them into one object.
    formValues: Record<string, unknown>;
    // The step names the user has saved at least once. Only steps with `requiresVisit` consult this.
    visitedSteps: ReadonlySet<string>;
    // Whether the user has already submitted a program application. The `submit_application` step
    // has no inputs, so submitting is what makes it complete.
    hasSubmittedApplication: boolean;
};
